import React, { useState } from 'react';
import { Alert, Button, Input, Radio, Space, Tag, Typography } from 'antd';
import { AnswerValue, Question } from './checklistTypes';
import EvidenceStrip from './EvidenceStrip';

const { Text } = Typography;

const LEVEL_COLOUR: { [level: string]: string } = {
  CRITICAL: 'red', MAJOR: 'volcano', MINOR: 'gold', CONCERN: 'blue',
};
const PRIORITY_COLOUR: { [p: string]: string } = { H: 'red', M: 'orange', L: 'default' };

interface Props {
  question: Question;
  indent: boolean;
  primaryFailed: boolean;
  disabled: boolean;
  onAnswer: (q: Question, answer: AnswerValue, remarks: string) => void;
  onEditNC: (q: Question) => void;
  canReview?: boolean;
  onReview?: (q: Question) => void;
  canFlag?: boolean;
  onFlag?: (q: Question) => void;
  onRemoveFlag?: (q: Question) => void;
  canAddress?: boolean;
  onAddress?: (q: Question, reply: string) => void;
  onUploadEvidence?: (q: Question, file: File) => Promise<void>;
  onRemoveEvidence?: (q: Question, evidenceId: string) => void;
  canUndo?: boolean;
  onUndo?: (q: Question) => void;
  onHistory?: (q: Question) => void;
  context?: string;
  parentText?: string;
}

const QuestionRow: React.FC<Props> = ({ question: q, indent, primaryFailed, disabled, onAnswer, onEditNC, canReview, onReview, canFlag, onFlag, onRemoveFlag, canAddress, onAddress,
  onUploadEvidence, onRemoveEvidence, canUndo, onUndo, onHistory, context, parentText,
}) => {
  const [remarks, setRemarks] = useState(q.remarks);
  const [reply, setReply] = useState('');

  return (
    <div
      style={{
        marginLeft: indent ? 32 : 0, marginBottom: 12, padding: 12, background: '#fff',
        border: '1px solid #f0f0f0', borderRadius: 6,
        borderLeft: q.answer === 'NO' ? '3px solid #ff4d4f' : undefined,
      }}
    >
      <Space align="start" style={{ width: '100%', justifyContent: 'space-between' }} wrap>
        <div style={{ flex: 1, minWidth: 240 }}>
          {context && <div><Text type="secondary" style={{ fontSize: 12 }}>{context}</Text></div>}
          {parentText && <div><Text type="secondary" style={{ fontSize: 12 }}>Under: {parentText}</Text></div>}
          <Text type="secondary" style={{ marginRight: 8 }}>{q.code}</Text>
          {q.starred && (
            <Button
              type="text" size="small" title="Returned by the reviewer: see the history"
              style={{ color: '#faad14', padding: '0 4px', marginRight: 4 }}
              onClick={() => onHistory && onHistory(q)}
            >
              ★
            </Button>
          )}
          {q.priority && <Tag color={PRIORITY_COLOUR[q.priority] || 'default'}>{q.priority}</Tag>}
          <div>{q.text}</div>
        </div>
        <Radio.Group
          value={q.answer}
          disabled={disabled}
          optionType="button"
          buttonStyle="solid"
          onChange={(e) => onAnswer(q, e.target.value, remarks)}
          options={[
            { value: 'YES', label: 'Yes' },
            { value: 'NO', label: 'No' },
            { value: 'NA', label: 'N/A' },
          ]}
        />
      </Space>

      {primaryFailed && (
        <div style={{ marginTop: 6 }}>
          <Text type="warning">The primary question is No, so a Yes here still scores 0. No separate NC is needed for that.</Text>
        </div>
      )}

      {q.answer && (
        <Input
          size="small" style={{ marginTop: 8 }} placeholder="Remarks (optional)"
          value={remarks} disabled={disabled}
          onChange={(e) => setRemarks(e.target.value)}
          onBlur={() => { if (remarks !== q.remarks) onAnswer(q, q.answer as AnswerValue, remarks); }}
        />
      )}

      {q.flag && (
        <Alert
          style={{ marginTop: 8 }} showIcon
          type={q.flag.status === 'OPEN' ? 'warning' : 'success'}
          message={q.flag.status === 'OPEN' ? `Flagged by ${q.flag.by}` : 'Addressed by the auditors'}
          description={
            <div>
              <div>{q.flag.note}</div>
              {q.flag.status === 'ADDRESSED' && q.flag.reply && <div><Text type="secondary">Reply: {q.flag.reply}</Text></div>}
              {q.flag.status === 'ADDRESSED' && canUndo && (
                <Button size="small" style={{ marginTop: 8 }} onClick={() => onUndo && onUndo(q)}>
                  Take back "addressed"
                </Button>
              )}
              {q.flag.status === 'OPEN' && canAddress && (
                <Space style={{ marginTop: 8, width: '100%' }} align="start">
                  <Input.TextArea
                    rows={2} style={{ width: 360 }} value={reply} placeholder="Reply (optional)"
                    onChange={(e) => setReply(e.target.value)}
                  />
                  <Button type="primary" size="small" onClick={() => onAddress && onAddress(q, reply)}>
                    Mark addressed
                  </Button>
                </Space>
              )}
            </div>
          }
        />
      )}

      {(canReview || canFlag) && q.answer && (
        <Space style={{ marginTop: 8 }} wrap>
          {canReview && <Button size="small" onClick={() => onReview && onReview(q)}>Change (reviewer)</Button>}
          {canFlag && (
            <Button size="small" onClick={() => onFlag && onFlag(q)}>
              {!q.flag ? 'Flag for auditors' : q.flag.status === 'ADDRESSED' ? 'Flag again' : 'Edit flag'}
            </Button>
          )}
          {canFlag && q.flag && (
            <Button size="small" onClick={() => onRemoveFlag && onRemoveFlag(q)}>
              {q.flag.status === 'ADDRESSED' ? 'Accept' : 'Withdraw flag'}
            </Button>
          )}
        </Space>
      )}

      {(q.answer === 'YES' || q.answer === 'NO') && (
        <EvidenceStrip
          evidence={q.evidence}
          required={q.answer === 'NO'}
          canEdit={!disabled}
          onUpload={(file) => (onUploadEvidence ? onUploadEvidence(q, file) : Promise.resolve())}
          onRemove={(id) => onRemoveEvidence && onRemoveEvidence(q, id)}
        />
      )}

      {q.answer === 'NO' && (
        <div style={{ marginTop: 8 }}>
          {q.nc ? (
            <Space wrap>
              <Tag color={LEVEL_COLOUR[q.nc.level]}>{q.nc.level}</Tag>
              <Text>{q.nc.object_id}</Text>
              <Text type="secondary">{q.nc.description.slice(0, 90)}{q.nc.description.length > 90 ? '...' : ''}</Text>
              {!disabled && <Button size="small" onClick={() => onEditNC(q)}>Edit NC</Button>}
            </Space>
          ) : (
            <Space>
              <Tag color="error">NC required</Tag>
              {!disabled && <Button size="small" type="primary" danger onClick={() => onEditNC(q)}>Raise NC</Button>}
            </Space>
          )}
        </div>
      )}
    </div>
  );
};

export default QuestionRow;
